import style from "../../style/CashFlow.module.scss";
import className from "classnames/bind";
import {Fragment, useState, useEffect, memo, useCallback} from "react";
import {Table, DatePicker, Modal, Tabs} from "antd";
import dayjs from "dayjs";
import ActionBar from "~/layout/ActionBar";
import {Loading} from "~/components";
import FontAwesomeIcon from "~/components/FontAwesome";
import {apiError, handleRequest, numberFormat, strToTime} from "~/utils";
import {reportApi} from "~/api";

const cn = className.bind(style);

//Hiển thị số: 0 -> '-'
const money = (value) => (value ? numberFormat(value) : '-');

//Màu chấm theo loại tài khoản
const typeColor = {
	cash:   '#16a34a',
	wallet: '#db2777',
	pos:    '#0284c7',
	bank:   '#4f46e5',
};

const emptyTotal = {opening: 0, totalIn: 0, totalOut: 0, closing: 0};

function CashFlowReport() {

	const [loading, setLoading] = useState(false);

	const [day, setDay] = useState(dayjs());

	const [accounts, setAccounts] = useState([]);

	const [total, setTotal] = useState(emptyTotal);

	const [detail, setDetail] = useState({
		open: false, loading: false, title: '',
		revenues: {rows: [], total: 0},
		receipts: {rows: [], total: 0},
		expenses: {rows: [], total: 0},
		salaries: {rows: [], total: 0},
	});

	const handleLoad = useCallback(async (d) => {
		setLoading(true);
		let [error, response] = await handleRequest(reportApi.cashFlow({time: strToTime(d)}));
		let message = apiError(`Lấy thông tin báo cáo dòng tiền thất bại`, error, response);
		if (!message) {
			setAccounts((response.data?.accounts || []).map((a) => ({...a, _k: a.key})));
			setTotal(response.data?.total || emptyTotal);
		}
		setLoading(false);
	}, []);

	useEffect(() => { handleLoad(day).then(); }, [day, handleLoad]);

	const openDetail = async (account, title) => {
		setDetail((d) => ({...d, open: true, loading: true, title}));
		const [error, response] = await handleRequest(reportApi.cashFlowDetail({account, time: strToTime(day)}));
		if (error) {
			setDetail((d) => ({...d, loading: false}));
			return;
		}
		const idx = (list) => ({rows: (list?.rows || []).map((r, i) => ({...r, _k: i})), total: list?.total || 0});
		setDetail((d) => ({
			...d,
			loading: false,
			revenues: idx(response.data?.revenues),
			receipts: idx(response.data?.receipts),
			expenses: idx(response.data?.expenses),
			salaries: idx(response.data?.salaries),
		}));
	};

	//Ô số click được -> mở modal chi tiết tài khoản
	const flowCell = (color) => (value, row) => {
		if (!value) return money(value);
		return (
			<span
				onClick={() => openDetail(row.key, row.title)}
				style={{cursor: 'pointer', color, textDecoration: 'underline dotted'}}
			>{money(value)}</span>
		);
	};

	const columns = [
		{
			title: 'Tài khoản',
			dataIndex: 'title',
			fixed: 'left',
			width: 220,
			render: (value, row) => (
				<div className={cn('account')}>
					<span className={cn('dot')} style={{background: typeColor[row.type] || '#94a3b8'}}/>
					{value}
				</div>
			),
		},
		{title: 'Số tài khoản', dataIndex: 'accountNumber', width: 130, align: 'center', render: (v) => v || '-'},
		{title: 'Số dư đầu ngày', dataIndex: 'opening', width: 150, align: 'right', render: (v) => <b>{money(v)}</b>},
		{title: 'Thu', dataIndex: 'totalIn', width: 150, align: 'right', render: flowCell('#16a34a')},
		{title: 'Chi', dataIndex: 'totalOut', width: 150, align: 'right', render: flowCell('#e11d48')},
		{
			title: 'Số dư cuối ngày',
			dataIndex: 'closing',
			fixed: 'right',
			width: 160,
			align: 'right',
			render: (value) => <b style={{color: value < 0 ? '#ef4444' : '#0284c7'}}>{money(value)}</b>,
		},
	];

	//Hàng tổng dưới chân bảng
	const summary = () => (
		<Table.Summary fixed>
			<Table.Summary.Row>
				<Table.Summary.Cell index={0} colSpan={2}><b>TỔNG</b></Table.Summary.Cell>
				<Table.Summary.Cell index={2} align="right"><b>{money(total.opening)}</b></Table.Summary.Cell>
				<Table.Summary.Cell index={3} align="right"><b style={{color: '#16a34a'}}>{money(total.totalIn)}</b></Table.Summary.Cell>
				<Table.Summary.Cell index={4} align="right"><b style={{color: '#e11d48'}}>{money(total.totalOut)}</b></Table.Summary.Cell>
				<Table.Summary.Cell index={5} align="right">
					<b style={{color: total.closing < 0 ? '#ef4444' : '#0284c7'}}>{money(total.closing)}</b>
				</Table.Summary.Cell>
			</Table.Summary.Row>
		</Table.Summary>
	);

	const detailColumns = [
		{title: 'Ngày', dataIndex: 'time', width: 110, render: (t) => t ? dayjs.unix(t).format('DD/MM/YYYY') : '-'},
		{title: 'Nội dung', dataIndex: 'label', width: 200},
		{title: 'Ghi chú', dataIndex: 'note'},
		{title: 'Số tiền', dataIndex: 'price', width: 140, align: 'right', render: money},
	];

	const detailTable = (color, data) => (
		<Table
			rowKey="_k"
			size="small"
			columns={detailColumns}
			dataSource={data.rows}
			pagination={false}
			scroll={{y: 340}}
			locale={{emptyText: 'Không có dữ liệu'}}
			summary={() => (
				<Table.Summary fixed>
					<Table.Summary.Row>
						<Table.Summary.Cell index={0} colSpan={3}><b>Tổng</b></Table.Summary.Cell>
						<Table.Summary.Cell index={1} align="right"><b style={{color}}>{money(data.total)}</b></Table.Summary.Cell>
					</Table.Summary.Row>
				</Table.Summary>
			)}
		/>
	);

	//Các tab chi tiết, nhãn kèm tổng tiền
	const detailTabs = [
		{key: 'revenues', icon: 'fa-light fa-sack-dollar', color: '#0284c7', title: 'Doanh thu', data: detail.revenues},
		{key: 'receipts', icon: 'fa-light fa-arrow-down-to-line', color: '#16a34a', title: 'Phiếu thu', data: detail.receipts},
		{key: 'expenses', icon: 'fa-light fa-arrow-up-from-line', color: '#e11d48', title: 'Phiếu chi', data: detail.expenses},
		{key: 'salaries', icon: 'fa-light fa-money-check-dollar-pen', color: '#d97706', title: 'Phiếu lương', data: detail.salaries},
	].map((t) => ({
		key: t.key,
		label: (
			<span>
				<FontAwesomeIcon icon={t.icon} style={{color: t.color, marginRight: 6}}/>
				{t.title} <b style={{color: t.color}}>({money(t.data.total)})</b>
			</span>
		),
		children: detailTable(t.color, t.data),
	}));

	const cards = [
		{icon: 'fa-light fa-piggy-bank', label: 'Số dư đầu ngày', value: total.opening, gradient: 'linear-gradient(135deg, #475569 0%, #64748b 60%, #94a3b8 100%)'},
		{icon: 'fa-light fa-arrow-down-to-line', label: 'Tổng thu', value: total.totalIn, gradient: 'linear-gradient(135deg, #047857 0%, #059669 60%, #34d399 100%)'},
		{icon: 'fa-light fa-arrow-up-from-line', label: 'Tổng chi', value: total.totalOut, gradient: 'linear-gradient(135deg, #be123c 0%, #e11d48 60%, #fb7185 100%)'},
		{icon: 'fa-light fa-wallet', label: 'Số dư cuối ngày', value: total.closing, gradient: 'linear-gradient(135deg, #0369a1 0%, #0284c7 60%, #38bdf8 100%)'},
	];

	return (
		<Fragment>
			<ActionBar title={'Báo cáo dòng tiền'}></ActionBar>
			<div className="container d-block">
				<div className={cn('page')}>

					<div className={cn('header')}>
						<DatePicker
							allowClear={false}
							format="DD/MM/YYYY"
							value={day}
							onChange={(d) => d && setDay(d)}
							style={{width: 170}}
						/>
					</div>

					<div className={cn('cards')}>
						{cards.map((c, i) => (
							<div key={i} className={cn('card')}>
								<div className={cn('icon')} style={{background: c.gradient}}>
									<FontAwesomeIcon icon={c.icon}/>
								</div>
								<div style={{minWidth: 0}}>
									<p className={cn('label')}>{c.label}</p>
									<p className={cn('value')}>{numberFormat(c.value)}</p>
									<p className={cn('unit')}>VND</p>
								</div>
							</div>
						))}
					</div>

					<div className={cn('panel')}>
						<div className={cn('panelHead')}>Số dư tài khoản</div>
						<div className={cn('panelBody')}>
							{loading && <Loading/>}
							{!loading &&
								<div className={cn('table')}>
									<Table
										rowKey="_k"
										size="small"
										bordered
										columns={columns}
										dataSource={accounts}
										pagination={false}
										scroll={{x: 'max-content'}}
										summary={summary}
									/>
								</div>
							}
						</div>
					</div>

				</div>
			</div>

			<Modal
				open={detail.open}
				title={`Chi tiết — ${detail.title}`}
				footer={null}
				width={820}
				onCancel={() => setDetail((d) => ({...d, open: false}))}
			>
				{detail.loading ? <Loading/> :
					<div className={cn('table')}>
						<Tabs items={detailTabs} defaultActiveKey="revenues"/>
					</div>
				}
			</Modal>
		</Fragment>
	)
}

export default memo(CashFlowReport);
